import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import dateFormat from "dateformat";
import { emptyStateIllustrations } from "../assets/empty-states";
import { Apis } from "../util/Apis";
import {
  Card,
  CardHeader,
  ComboMachine,
  EmptyState,
  Loading,
  PageWrapper,
  Toast,
} from "../components/blocks";
import { LuArrowLeftRight, LuHistory, LuWallet } from "react-icons/lu";
import { Button, Input, Label, Modal } from "../components/atoms";

export const formatNumber = (number, symbol, digitos = 2) => {
  let parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    parsedNumber = 0;
  }

  if (symbol == null) {
    digitos = 0;
  }

  const formattedNumber = parsedNumber.toLocaleString("en-US", {
    minimumFractionDigits: digitos,
    maximumFractionDigits: digitos,
  });

  if (symbol == null) {
    return formattedNumber;
  }

  return `${symbol} ${formattedNumber}`;
};

const Cashless = () => {
  const salaInfo = JSON.parse(localStorage.getItem("salaInfo"));
  const [amount, setAmount] = useState("");
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastVariant, setToastVariant] = useState("error");

  const {
    data,
    isLoading: loading,
    refetch: fetchData,
  } = useQuery({
    queryKey: ["cashless", salaInfo?.salaId],
    queryFn: async () => {
      const res = await axios.get(Apis.CAPP + "/cashless?salaId=" + salaInfo.salaId);
      return res.data;
    },
    enabled: !!salaInfo?.salaId,
  });

  const { data: history = [], refetch: fetchDataHistory } = useQuery({
    queryKey: ["cashlessHistory", salaInfo?.salaId],
    queryFn: async () => {
      const res = await axios.get(Apis.CAPP + "/cashless/history?salaId=" + salaInfo.salaId);
      return res.data;
    },
    enabled: !!salaInfo?.salaId,
  });

  const transferMutation = useMutation({
    mutationFn: async ({ amount, machineId }) => {
      await axios.post(Apis.CAPP + "/cashless", {
        confId: parseInt(machineId),
        amount: parseFloat(amount),
        salaId: salaInfo.salaId,
      });
    },
    onSuccess: () => {
      setToastMsg("Transferencia exitosa");
      setToastVariant("success");
      setToastOpen(true);
      setAmount("");
      setSelectedMachine("");
      fetchData();
      fetchDataHistory();
      setIsOpen(false);
    },
    onError: () => {
      setToastMsg("Ocurrio un error al transferir");
      setToastVariant("error");
      setToastOpen(true);
      setAmount("");
      setSelectedMachine("");
    },
  });

  const cashoutMutation = useMutation({
    mutationFn: async () => {
      await axios.post(Apis.CAPP + "/cashless/cashout?salaId=" + salaInfo.salaId);
    },
    onSuccess: () => {
      setToastMsg("Cobro exitoso");
      setToastVariant("success");
      setToastOpen(true);
      fetchData();
      setIsOpen(false);
    },
    onError: () => {
      setToastMsg("Ocurrio un error al cobrar creditos");
      setToastVariant("error");
      setToastOpen(true);
    },
  });

  const handleCancel = () => {
    setAmount("");
    setSelectedMachine(null);
    setIsOpen(false);
  };

  const handleTransfer = () => {
    if (!amount || !selectedMachine?.confId) {
      setToastMsg("Completa todos los campos");
      setToastVariant("error");
      setToastOpen(true);
      return;
    }
    transferMutation.mutate({ amount: amount, machineId: selectedMachine.confId });
  };

  return (
    <PageWrapper>
      {loading ? (
        <Loading />
      ) : (
        <div className="flex flex-col w-full gap-4">
          <Card>
            <CardHeader
              title="Billetera digital"
              subtitle="Saldo actual"
              icon={<LuWallet size={24} />}
            />
            <div className="flex flex-row items-end justify-end w-full mb-2">
              <span className="mr-2 text-xl font-extrabold leading-tight">
                {"S/ " + formatNumber(data?.currentAmount || 0)}
              </span>
            </div>
            <div className="flex flex-row w-full gap-2">
              <Button
                variant="outline"
                onClick={() => cashoutMutation.mutate()}
                className="w-full"
                disabled={loading || cashoutMutation.isLoading}
              >
                {cashoutMutation.isLoading ? "Cobrando..." : "Cobrar"}
              </Button>
              <Button onClick={() => setIsOpen(true)} className="w-full">
                Transferir
              </Button>
            </div>
          </Card>

          {history?.length > 0 ? (
            <Card>
              <CardHeader
                title="Historial"
                subtitle="Ultimas transferencias realizadas"
                icon={<LuHistory size={24} />}
              />
              <div className="flex flex-col w-full gap-2">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-row items-center justify-between py-2"
                  >
                    <span className="flex-1 text-sm text-slate-600">
                      {dateFormat(item.createdAt, "yyyy-mm-dd hh:MM TT")}
                    </span>
                    <span className="text-xl font-bold">S/ {item.amount}</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <EmptyState
              title="No hay transferencias recientes"
              subtitle="Pregunte a un administrador por mas informacion"
              illustration={emptyStateIllustrations.transfers}
              illustrationAlt="Sin transferencias recientes"
            />
          )}
        </div>
      )}
      <Modal open={isOpen} onClose={handleCancel}>
        <div className="flex flex-col items-center">
          <CardHeader
            title="Transferir saldo"
            subtitle="Seleccione una maquina y el monto a transferir"
            icon={<LuArrowLeftRight size={24} />}
          />
          <form
            className="flex flex-col w-full gap-4 pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleTransfer();
            }}
          >
            <div className="flex flex-col w-full">
              <Label>Monto a transferir</Label>
              <Input
                type="number"
                placeholder="Ej. 20"
                value={amount}
                autoFocus
                onChange={(e) => setAmount(e.target.value)}
                className="w-100"
              />
            </div>
            <ComboMachine
              machineSelected={selectedMachine}
              setMachineSelected={setSelectedMachine}
            />
            <div className="flex flex-col w-full gap-3 mt-4">
              <Button disabled={loading || transferMutation.isLoading} type="submit">
                {loading || transferMutation.isLoading ? "Cargando" : "Transferir"}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Toast
        open={toastOpen}
        text={toastMsg}
        variant={toastVariant}
        duration={2500}
        onClose={() => setToastOpen(false)}
      />
    </PageWrapper>
  );
};

export default Cashless;
